import React, { FC, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react'

import classNames from 'classnames'

enum Direction {
  LEFT,
  RIGHT,
}

interface Props {
  children: React.ReactNode[]
  className?: string
}

/**
 * Side-scrolling carousel-style wrapper. Use by wrapping an array of same-sized child components.
 */
export const SideScrollContainer: FC<Props> = ({ children, className }: Props) => {
  const [hover, setHover] = useState(false)
  const [itemCount] = useState(children.length)
  const [itemWidth, setItemWidth] = useState(0)
  const [scrollPosition, setScrollPosition] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)

  const { scrollWidth, scrollLeft, clientWidth } = containerRef.current || {
    scrollWidth: 0,
    scrollLeft: 0,
    clientWidth: 0,
  }

  // when the user clicks the left and right arrow buttons
  const scroll = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, dir: Direction) => {
      e.preventDefault()

      const currIndex = scrollPosition / itemWidth

      let newIndex: number | null = null
      if (dir === Direction.LEFT) {
        const flooredIndex = Math.floor(currIndex)
        newIndex = flooredIndex === currIndex ? currIndex - 1 : flooredIndex
      } else if (dir === Direction.RIGHT) {
        const ceilIndex = Math.ceil(currIndex)
        newIndex = ceilIndex === currIndex ? currIndex + 1 : ceilIndex
      }

      const container = containerRef.current
      if (newIndex !== null && typeof container?.scrollTo === 'function')
        container.scrollTo({ left: newIndex * itemWidth, behavior: 'smooth' })
    },
    [scrollPosition, itemWidth]
  )

  // listens for changes to the container internal width
  // and number of child items, updates the calculated item width
  useEffect(() => {
    if (scrollWidth && itemCount) setItemWidth(scrollWidth / itemCount)
  }, [scrollWidth, itemCount])

  // add scrolling listener to container
  useEffect(() => {
    const container = containerRef.current

    // when the user scrolls or drags the container
    const onMouseEvt = (): void => {
      if (container) setScrollPosition(container.scrollLeft)
    }

    container?.addEventListener('scroll', onMouseEvt)
    container?.addEventListener('mouseenter', onMouseEvt)
    return () => {
      container?.removeEventListener('scroll', onMouseEvt)
      container?.removeEventListener('mouseenter', onMouseEvt)
    }
  }, [])

  return (
    <div
      className="relative flex items-end"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      {scrollLeft > 0 && (
        <ScrollButton onClick={(e) => scroll(e, Direction.LEFT)} direction="left" show={hover} />
      )}
      <div
        className={classNames('w-full overflow-x-auto whitespace-nowrap', className)}
        ref={containerRef}>
        {children}
      </div>
      {(!clientWidth || scrollWidth - scrollLeft > clientWidth) && (
        <ScrollButton
          onClick={(e) => scroll(e, Direction.RIGHT)}
          direction="right"
          show={hover || !clientWidth}
        />
      )}
    </div>
  )
}

interface ScrollButtonProps {
  direction: 'left' | 'right'
  onClick: MouseEventHandler<HTMLButtonElement>
  show: boolean
}

export const ScrollButton: FC<ScrollButtonProps> = ({
  direction,
  onClick,
  show,
}: ScrollButtonProps): JSX.Element => {
  return (
    <button
      className={classNames(
        `absolute m-5 rounded-full border-white border bg-black bg-opacity-50 w-16 h-16 md:w-[72px] md:h-[72px] transition-opacity z-10`,
        show ? 'opacity-100' : 'lg:opacity-0',
        {
          'left-0': direction === 'left',
          'right-0': direction === 'right',
        }
      )}
      onClick={onClick}>
      <i
        className={classNames('text-white text-h2 font-light nf-icon', `nf-chevron-${direction}`)}
      />
    </button>
  )
}
